import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  LinearProgress,
  Button,
  Chip,
  Grid,
  TextField,
  InputAdornment,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { colors, shadows } from "../source/source.styles";
import NoDataFound from "../../Reports/NoDataFound";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhoneIcon from "@mui/icons-material/Phone";
import ChatIcon from "@mui/icons-material/Chat";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import PeopleIcon from "@mui/icons-material/People";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const SourceLeadDetails = ({ source, onBack }) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");

  // Mock data - replace with actual API call
  const leadsData = [
    {
      date: "22 Oct 2023",
      leadName: "Anamya Kapoor",
      initials: "AK",
      contact: { phone: true, whatsapp: true },
      location: "Mumbai",
      treatmentType: "Skin Care",
      telecaller: { name: "Priya S.", avatar: null },
      status: "Interested",
      statusColor: "#10B981",
      aiConfidence: 88,
    },
    {
      date: "21 Oct 2023",
      leadName: "Rahul Verma",
      initials: "RV",
      contact: { phone: true, whatsapp: true },
      location: "Delhi",
      treatmentType: "Hair Loss",
      telecaller: { name: "Vikram R.", avatar: null },
      status: "New",
      statusColor: "#3B82F6",
      aiConfidence: 75,
    },
    {
      date: "20 Oct 2023",
      leadName: "Sneha Mishra",
      initials: "SM",
      contact: { phone: true, whatsapp: true },
      location: "Bangalore",
      treatmentType: "Skin Care",
      telecaller: { name: "Priya S.", avatar: null },
      status: "Contacted",
      statusColor: "#F59E0B",
      aiConfidence: 92,
    },
  ];

  // Filter data based on search query
  const filteredLeads = leadsData.filter(
    (lead) =>
      lead.leadName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.treatmentType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.telecaller.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const headerCellSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: colors.textMuted,
    textTransform: "uppercase",
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2,
    bgcolor: colors.bgPrimary,
  };

  const bodyCellSx = {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textPrimary,
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2.5,
  };

  const getAvatarColor = (name) => {
    const colors = ["#C4B5FD", "#FCA5A5", "#A7F3D0", "#FDE68A", "#BAE6FD"];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Box sx={{ bgcolor: colors.bgPrimary, minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderBottom: `1px solid ${colors.border}`,
          p: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <IconButton
            onClick={onBack}
            sx={{
              color: colors.textSecondary,
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Stack spacing={0.5} flex={1}>
            {/* Breadcrumb */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  cursor: "pointer",
                  "&:hover": { color: colors.primary },
                }}
              >
                HOME
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                ›
              </Typography>
              <Typography
                component="span"
                onClick={onBack}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  cursor: "pointer",
                  "&:hover": { color: colors.primary },
                }}
              >
                DASHBOARD
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                ›
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.primary,
                  letterSpacing: 0.5,
                }}
              >
                LEAD DETAILS
              </Typography>
            </Stack>

            {/* Title */}
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 700,
                color: colors.textPrimary,
              }}
            >
              Leads by {source?.name || "Source"}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              placeholder="Search leads..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 240,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: 13,
                  bgcolor: colors.bgPrimary,
                  "& fieldset": {
                    borderColor: colors.borderLight,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.border,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 1,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  py: 1,
                },
              }}
            />
            <IconButton
              sx={{
                color: colors.textMuted,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 2,
                "&:hover": {
                  bgcolor: colors.bgPrimary,
                  borderColor: colors.border,
                },
              }}
            >
              <TuneIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Metrics */}
        <Grid container spacing={3} mt={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                p: 3,
                border: `1px solid ${colors.borderLight}`,
                boxShadow: shadows.card,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textMuted,
                      letterSpacing: 0.5,
                      mb: 1,
                    }}
                  >
                    TOTAL LEADS FROM THIS SOURCE
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 32,
                      fontWeight: 700,
                      color: colors.textPrimary,
                    }}
                  >
                    {source?.totalLeads || 542}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
              </Stack>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                p: 3,
                border: `1px solid ${colors.borderLight}`,
                boxShadow: shadows.card,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textMuted,
                      letterSpacing: 0.5,
                      mb: 1,
                    }}
                  >
                    AVG. LEAD QUALITY
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 32,
                      fontWeight: 700,
                      color: colors.textPrimary,
                    }}
                  >
                    88/100
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "#DBEAFE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrackChangesIcon sx={{ fontSize: 24, color: "#2563EB" }} />
                </Box>
              </Stack>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                p: 3,
                border: `1px solid ${colors.borderLight}`,
                boxShadow: shadows.card,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textMuted,
                      letterSpacing: 0.5,
                      mb: 1,
                    }}
                  >
                    CONVERSION RATE
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 32,
                      fontWeight: 700,
                      color: colors.textPrimary,
                    }}
                  >
                    {source?.conversionRate || "18.4"}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "#D1FAE5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 24, color: "#10B981" }} />
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Leads Table */}
      <Box sx={{ p: 4 }}>
        <Box
          sx={{
            bgcolor: "#FFFFFF",
            borderRadius: 3,
            border: `1px solid ${colors.borderLight}`,
            boxShadow: shadows.card,
            overflow: "hidden",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Date</TableCell>
                <TableCell sx={headerCellSx}>Lead Name</TableCell>
                <TableCell sx={headerCellSx}>Contact</TableCell>
                <TableCell sx={headerCellSx}>Location</TableCell>
                <TableCell sx={headerCellSx}>Treatment Type</TableCell>
                <TableCell sx={headerCellSx}>Telecaller</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>AI Confidence</TableCell>
                <TableCell sx={headerCellSx}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLeads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ border: "none" }}>
                    <NoDataFound />
                  </TableCell>
                </TableRow>
              ) : (
                filteredLeads.map((lead, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": {
                      bgcolor: colors.bgPrimary,
                    },
                  }}
                >
                  <TableCell sx={bodyCellSx}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: colors.textSecondary,
                      }}
                    >
                      {lead.date}
                    </Typography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: getAvatarColor(lead.leadName),
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {lead.initials}
                      </Avatar>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.textPrimary,
                        }}
                      >
                        {lead.leadName}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: colors.bgPrimary,
                          "&:hover": { bgcolor: colors.border },
                        }}
                      >
                        <PhoneIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: colors.bgPrimary,
                          "&:hover": { bgcolor: colors.border },
                        }}
                      >
                        <ChatIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                      </IconButton>
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: colors.textPrimary,
                      }}
                    >
                      {lead.location}
                    </Typography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Chip
                      label={lead.treatmentType}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: lead.treatmentType === "Skin Care" ? alpha(theme.palette.primary.main, 0.15) : "#DBEAFE",
                        color: lead.treatmentType === "Skin Care" ? theme.palette.primary.main : "#2563EB",
                      }}
                    />
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: 11,
                          bgcolor: colors.primary,
                        }}
                      >
                        {lead.telecaller.name.charAt(0)}
                      </Avatar>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: colors.textPrimary,
                        }}
                      >
                        {lead.telecaller.name}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Chip
                      label={lead.status}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: `${lead.statusColor}15`,
                        color: lead.statusColor,
                      }}
                    />
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Box sx={{ width: 120 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LinearProgress
                          variant="determinate"
                          value={lead.aiConfidence}
                          sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 1,
                            bgcolor: colors.bgPrimary,
                            "& .MuiLinearProgress-bar": {
                              bgcolor: colors.primary,
                              borderRadius: 1,
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: colors.textPrimary,
                            minWidth: 30,
                          }}
                        >
                          {lead.aiConfidence}%
                        </Typography>
                      </Stack>
                    </Box>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Button
                      variant="text"
                      size="small"
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: colors.primary,
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: colors.bgPrimary,
                        },
                      }}
                    >
                      View Full Profile
                    </Button>
                  </TableCell>
                </TableRow>
              ))
              )}
            </TableBody>
          </Table>

          {/* Footer */}
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${colors.borderLight}`,
              bgcolor: colors.bgPrimary,
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: colors.textSecondary,
                textAlign: "center",
              }}
            >
              Showing {filteredLeads.length} of {source?.totalLeads || leadsData.length} leads from {source?.name || "this"} source
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SourceLeadDetails;
